import * as THREE from "three";
import { Suspense, useRef, useState, useEffect } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";
import { Leva } from "leva";
import SkyWithCloud from "./SkyWithClouds.jsx";
import CustomCursor from "./CustomCursor.jsx";
import Joystick from "./Joystick.jsx";
import ModelBackground from "./ModelBackground.jsx";
import BuildingCarousel from "./BuildingCarousel.jsx";
import LensFlare from "./LensFlare.jsx";

// -------------------- Smooth Camera Rig --------------------
function Rig({
  moveCamera,
  setMoveCamera,
  targetPosition,
  targetLookAt,
  onComplete,
  isInitialLerp,
}) {
  const { camera } = useThree();
  const controls = useThree((state) => state.controls);

  // We'll capture the starting distance for the initial cinematic lerp
  // so we can compute progress and increase the lerp speed (ease-out)
  const initialDistanceRef = useRef(null);

  useEffect(() => {
    // reset when movement stops
    if (!moveCamera) {
      initialDistanceRef.current = null;
    } else if (moveCamera && isInitialLerp && initialDistanceRef.current == null) {
      // capture the distance once at the start of the initial lerp
      initialDistanceRef.current = camera.position.distanceTo(targetPosition);
    }
  }, [moveCamera, isInitialLerp, targetPosition, camera.position]);

  useFrame(() => {
    if (moveCamera) {
      if (isInitialLerp) {
        // compute progress from start -> target (0..1)
        const currentDistance = camera.position.distanceTo(targetPosition);
        const total = initialDistanceRef.current || currentDistance || 1;
        const rawProgress = Math.min(1, Math.max(0, 1 - currentDistance / total));

        // ease-out (quadratic): easeOutQuad(t) = t * (2 - t)
        const eased = rawProgress * (2 - rawProgress);

        // start slow, speed up towards the end
        const startSpeed = 0.01; // slower initial increment per frame
        const endSpeed = 0.04; // faster near the end
        const lerpSpeed = startSpeed + (endSpeed - startSpeed) * eased;

        camera.position.lerp(targetPosition, lerpSpeed);

        if (controls && controls.target && targetLookAt) {
          controls.target.lerp(targetLookAt, lerpSpeed);
          controls.update();
        }
      } else {
        // Normal (non-intro) lerp speed
        const lerpSpeed = 0.03;
        camera.position.lerp(targetPosition, lerpSpeed);

        if (controls && controls.target && targetLookAt) {
          controls.target.lerp(targetLookAt, lerpSpeed);
          controls.update();
        }
      }

      if (camera.position.distanceTo(targetPosition) < 0.1) {
        setMoveCamera(false);
        initialDistanceRef.current = null;
        if (onComplete) onComplete();
      }
    }
  });

  return null;
}

// -------------------- Main Experience --------------------
export default function Experience({ showOverlay }) {
  const [moveCamera, setMoveCamera] = useState(false);
  const [isInitialLerp, setIsInitialLerp] = useState(false); // ← NEW: Track intro lerp
  const [overlayActive, setOverlayActive] = useState(false);
  const [targetPosition, setTargetPosition] = useState(
    new THREE.Vector3(0, 0, 9)
  );
  const [targetLookAt, setTargetLookAt] = useState(new THREE.Vector3(0, 0, 0));
  const [selectedModelName, setSelectedModelName] = useState(null);
  const [showButtons, setShowButtons] = useState(false);
  const [fadeInButtons, setFadeInButtons] = useState(false);
  const controlsRef = useRef();
  const modelRefs = useRef([]);
  const brandenburgRef = useRef();
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const ua = navigator.userAgent;
      setIsMobile(
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)
      );
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Trigger overlay fade-in after loader completes

  useEffect(() => {
    // Trigger animation right after component mounts
    const timer = setTimeout(() => setOverlayActive(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Joystick handler
  const [joystickZoomEnabled, setJoystickZoomEnabled] = useState(true);
  const handleJoystickMove = ({ x, y }) => {
    if (controlsRef.current) {
      if (isMobile && moveCamera) setMoveCamera(false);
      const speed = 0.25;
      const camera = controlsRef.current.object;
      const target = controlsRef.current.target;
      const forward = new THREE.Vector3()
        .subVectors(target, camera.position)
        .setY(0)
        .normalize();
      const right = new THREE.Vector3()
        .crossVectors(forward, new THREE.Vector3(0, 1, 0))
        .normalize();

      camera.position.addScaledVector(forward, -y * speed);
      camera.position.addScaledVector(right, x * speed);
      target.addScaledVector(forward, -y * speed);
      target.addScaledVector(right, x * speed);
      controlsRef.current.update();

      if (y < -0.7 && !joystickZoomEnabled) {
        setJoystickZoomEnabled(true);
      } else if (y > 0.7 && joystickZoomEnabled) {
        setJoystickZoomEnabled(false);
      }
    }
  };

  // Select building
const handleSelect = (center, radius, name) => {
  const fov = 20 * (Math.PI / 180);
  // Increase the distance multiplier from 0.9 to 1.5 and increase the minimum distance from 4 to 6
  const distance = Math.max(6, (radius / Math.tan(fov / 2)) * 1.5);

  // Adjust the camera height and angle slightly
  const dir = new THREE.Vector3(0, 0.4, 1).normalize();

  const newPos = new THREE.Vector3()
    .copy(center)
    .addScaledVector(dir, distance);

  // Reset and start new animation even if already animating
  setMoveCamera(false);
  setIsInitialLerp(false);

  setTimeout(() => {
    setTargetPosition(newPos);
    setTargetLookAt(center.clone());
    setMoveCamera(true);
    setSelectedModelName(name);
  }, 0);

  setOverlayActive(false);
};

  // Enter button - Initial cinematic lerp
  const handleEnter = () => {
    setMoveCamera(true);
    setIsInitialLerp(true); // ← This is the intro animation
    setOverlayActive(false);
    setShowButtons(false);
    setFadeInButtons(false);

    setTimeout(() => {
      setShowButtons(true);
      setTimeout(() => setFadeInButtons(true), 50);
    }, 2000);
  };

  // Adjust zoom limits after camera lerp completes
  const handleLerpComplete = () => {
    if (controlsRef.current) {
      const distance = controlsRef.current.object.position.distanceTo(
        controlsRef.current.target
      );
      controlsRef.current.minDistance = Math.max(0.1, distance - 5);
      controlsRef.current.maxDistance = distance + 5;
      controlsRef.current.update();
    }
    setIsInitialLerp(false); // ← Reset after intro lerp
  };

  return (
    <div className="relative w-screen h-screen overflow-hidden">
      <CustomCursor />

      <Canvas shadows dpr={[1, 2]} camera={{ position: [0, 0.08, 50], fov: 20 }}>
        <Suspense fallback={null}>
          <SkyWithCloud />
          <ModelBackground
            onSelect={handleSelect}
            modelRefs={modelRefs}
            selectedModelName={selectedModelName}
            controlsRef={controlsRef}
          />
          <Environment
            files="/images/sky2.hdr"
            backgroundRotation={[0, 0.5236, 0]}
            environmentRotation={[0, 0.5236, 0]}
            background={true}
          />
          <Environment
            files="/images/lake_pier_2k.hdr"
            backgroundRotation={[0, 0.5236, 0]}
            environmentRotation={[0, 0, 0]}
            background={false}
          />
          <LensFlare />

          <Rig
            moveCamera={moveCamera}
            setMoveCamera={setMoveCamera}
            targetPosition={targetPosition}
            targetLookAt={targetLookAt}
            onComplete={handleLerpComplete}
            isInitialLerp={isInitialLerp}
          />
        </Suspense>

        {/* OrbitControls: DISABLED only during initial "Enter" lerp */}
        <OrbitControls
          ref={controlsRef}
          makeDefault
          enabled={!isInitialLerp} // ← ONLY disabled during intro
          enableZoom={!isInitialLerp && (isMobile ? joystickZoomEnabled : true)}
          enablePan={!isInitialLerp} // ← Enable pan after initial lerp
          enableDamping={!isInitialLerp}
          dampingFactor={0.08}
          minDistance={2}
          maxDistance={100}
          minPolarAngle={Math.PI / 2}
          maxPolarAngle={Math.PI / 2}
          onStart={() => {
            if (!isInitialLerp) {
              setMoveCamera(false); // Allow interruption only after intro
            }
          }}
        />
      </Canvas>

      <Leva collapsed={false} oneLineLabels={false} />

      {/* Overlay */}
      <div
        className={`overlay-fade absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6 transition-all duration-1000 ${
          overlayActive ? "opacity-100 scale-100" : "opacity-0 scale-125 pointer-events-none"
        } bg-cover bg-center bg-no-repeat ${showOverlay ? 'start-animations' : ''}`}
        style={{
          backgroundImage: "url('/MGD-Letzte Szene.png')",
        }}
      >
        <img
          src="/images/LOGO.png"
          alt="Logo"
          className="w-80 md:w-[19rem] drop-shadow-lg absolute top-1 fade-in"
        />
        <h2
          className="
    text-xl sm:text-3xl md:text-4xl lg:text-4xl
    font-serif font-light
    tracking-[0.15em] sm:tracking-[0.25em] md:tracking-[0.35em]
    uppercase text-white text-center drop-shadow-lg mt-10 px-4
    fade-in fade-in-delay-1
  "
        >
          <span className="font-bold text-purple-900">X</span> Kommunikation <br />
          <span className="font-bold text-purple-900">X</span> Digitales Touchpointmanagement <br />
          <span className="font-bold text-purple-900">X</span> Marketing as a Service <br />
          <span className="font-bold text-purple-900">X</span> Das Internet von morgen 
        </h2>

        <h4 style={{ marginTop: "3vh" }} className="fade-in fade-in-delay-2">
          Herzlich willkommen bei der Mediengruppe Deutschland
        </h4>

        <button
          onClick={handleEnter}
          className="mt-8 px-8 py-3 border border-white text-white rounded-md font-medium tracking-wide hover:bg-white hover:text-black transition fade-in fade-in-delay-3"
        >
          Neue Perspektive
        </button>
      </div>


      {/* Building Carousel */}
      {showButtons && (
        <BuildingCarousel
          showButtons={showButtons}
          fadeInButtons={fadeInButtons}
          brandenburgRef={brandenburgRef}
          modelRefs={modelRefs}
          handleSelect={handleSelect}
        />
      )}

      {/* Joystick (mobile only, hidden during intro lerp) */}
      {isMobile && showButtons && <Joystick onMove={handleJoystickMove} />}

    </div>
  );
}